import {
	Avatar,
	AvatarFallback,
	AvatarImage,
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	SidebarTrigger,
	Spinner,
	TintedBackgroundIcon,
	useSidebar,
} from "@monbits/ui";
import { LogOut, type LucideIcon, Settings } from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "src/components/logo";
import { ThemeToggle } from "src/components/theme-toggle";
import type { User } from "src/types";

export interface MenuItem {
	path: string;
	name: string;
	icon?: LucideIcon;
	color?: string;
	onClick?: () => void;
}

interface AppLayoutProps {
	children: React.ReactNode;
	menus: MenuItem[];
	bottomMenus?: MenuItem[];
	user?: User;
	merchantId?: string;
	merchants?: Array<{ id: string; name: string }>;
	onMerchantChange?: (merchantId: string) => void;
	onLogout: () => void;
	showMerchantSelector?: boolean;
	loading?: boolean;
}

// Pure helper — hoisted out of NavUser so it isn't recreated on every render.
function getUserInitials(name?: string): string {
	if (!name) return "?";
	const parts = name.split(" ");
	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	}
	return name.substring(0, 2).toUpperCase();
}

// Constant style for the sidebar brand-glow div. Hoisting prevents a new
// object reference on every AppLayout render, which would otherwise force
// React to diff the style prop and the browser to re-evaluate inline CSS.
const SIDEBAR_GLOW_STYLE: React.CSSProperties = {
	background:
		"radial-gradient(circle 600px at 0% 0%, var(--sidebar-brand-glow) 0%, color-mix(in oklch, var(--sidebar-brand-glow), transparent 50%) 25%, color-mix(in oklch, var(--sidebar-brand-glow), transparent 80%) 55%, transparent 80%)",
	zIndex: -1,
};

const NavUser = React.memo(function NavUser({
	user,
	onLogout,
}: {
	user?: User;
	onLogout: () => void;
}) {
	const { isMobile } = useSidebar();
	const navigate = useNavigate();

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="h-8 w-8 rounded-lg">
								<AvatarImage src={user?.profileImageUrl} alt={user?.name} />
								<AvatarFallback className="rounded-lg">
									{getUserInitials(user?.name)}
								</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">
									{user?.name || "User"}
								</span>
								<span className="text-muted-foreground truncate text-xs">
									{user?.email}
								</span>
							</div>
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="p-0 font-normal">
							<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
								<Avatar className="h-8 w-8 rounded-lg">
									<AvatarImage src={user?.profileImageUrl} alt={user?.name} />
									<AvatarFallback className="rounded-lg">
										{getUserInitials(user?.name)}
									</AvatarFallback>
								</Avatar>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-medium">
										{user?.name || "User"}
									</span>
									<span className="text-muted-foreground truncate text-xs">
										{user?.email}
									</span>
								</div>
							</div>
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={() => navigate("/account-settings")}>
							<Settings className="mr-2 h-4 w-4" />
							Account Settings
						</DropdownMenuItem>
						<DropdownMenuItem onClick={onLogout}>
							<LogOut className="mr-2 h-4 w-4" />
							Log out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
});

/**
 * Memoized list of sidebar navigation buttons. Re-renders only when the
 * menus array or the active pathname changes — prevents the 8 nav buttons
 * (each with TintedBackgroundIcon + tooltip) from rebuilding on every
 * AppLayout render. The active-state recompute is the cheap part; the
 * skipped DOM diff is the win.
 */
const SidebarNavList = React.memo(function SidebarNavList({
	menus,
	pathname,
}: {
	menus: MenuItem[];
	pathname: string;
}) {
	const navigate = useNavigate();

	return (
		<SidebarMenu className="gap-1.5">
			{menus.map((item) => {
				const Icon = item.icon;
				const isActive =
					pathname === item.path ||
					(item.path !== "/dashboard" && pathname.startsWith(item.path));
				return (
					<SidebarMenuItem key={item.path}>
						<SidebarMenuButton
							onClick={item.onClick ? item.onClick : () => navigate(item.path)}
							isActive={isActive}
							tooltip={item.name}
							className="h-10 px-3 group-data-[collapsible=icon]:justify-center! group-data-[collapsible=icon]:p-0!"
						>
							{Icon && item.color ? (
								<TintedBackgroundIcon
									icon={Icon}
									color={item.color}
									size="sm"
								/>
							) : Icon ? (
								<Icon className="h-4 w-4" />
							) : null}
							<span className="group-data-[collapsible=icon]:hidden">
								{item.name}
							</span>
						</SidebarMenuButton>
					</SidebarMenuItem>
				);
			})}
		</SidebarMenu>
	);
});

export function AppLayout({
	children,
	menus,
	bottomMenus,
	user,
	merchantId,
	merchants,
	onMerchantChange,
	onLogout,
	showMerchantSelector = true,
	loading = false,
}: AppLayoutProps) {
	const location = useLocation();
	const navigate = useNavigate();
	const pathname = location.pathname;

	const currentPage = React.useMemo(
		() =>
			[...menus, ...(bottomMenus ?? [])].find((item) => item.path === pathname),
		[menus, bottomMenus, pathname],
	);

	return (
		<SidebarProvider>
			<Sidebar
				collapsible="icon"
				className="[&_[data-slot=sidebar-container]]:!fixed [&_[data-slot=sidebar-container]]:!inset-y-0 [&_[data-slot=sidebar]]:!bg-transparent [&_[data-slot=sidebar-inner]]:!bg-transparent"
			>
				{/* Subtle top glow effect - behind logo
				    No filter:blur — the radial-gradient stops produce softness via
				    transparency falloff. Painting an 800x800 region without the
				    expensive blur filter is ~4x cheaper to render. The style
				    object is hoisted to a constant so the reference is stable
				    across re-renders. */}
				<div
					className="pointer-events-none absolute left-0 top-0 w-[800px] h-[800px] opacity-[0.18] dark:opacity-[0.28]"
					aria-hidden="true"
					style={SIDEBAR_GLOW_STYLE}
				/>
				<SidebarHeader className="h-[64px] border-b-1 flex items-center justify-center">
					<div className="group-data-[collapsible=icon]:hidden">
						<Logo variant="full" size="md" />
					</div>
					<div className="hidden group-data-[collapsible=icon]:block">
						<Logo variant="icon" size="md" />
					</div>
				</SidebarHeader>

				<SidebarContent>
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarNavList menus={menus} pathname={pathname} />
						</SidebarGroupContent>
					</SidebarGroup>
					{bottomMenus && bottomMenus.length > 0 && (
						<SidebarGroup className="mt-auto">
							<SidebarGroupContent>
								<SidebarNavList menus={bottomMenus} pathname={pathname} />
							</SidebarGroupContent>
						</SidebarGroup>
					)}
				</SidebarContent>

				<SidebarFooter>
					{showMerchantSelector && merchants && (
						<div className="px-3 py-2 pb-0 group-data-[collapsible=icon]:hidden">
							<div className="mb-2">
								<div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									Current Merchant
								</div>
							</div>
							<Select
								value={merchantId}
								onValueChange={(value) => {
									if (value !== "loading..." && onMerchantChange) {
										onMerchantChange(value);
									}
								}}
							>
								<SelectTrigger className="w-full h-10">
									<SelectValue placeholder="Select merchant" />
								</SelectTrigger>
								<SelectContent>
									{merchants && merchants.length > 0 ? (
										merchants.map((merchant) => (
											<SelectItem key={merchant.id} value={merchant.id}>
												{merchant.name}
											</SelectItem>
										))
									) : (
										<SelectItem value="loading..." disabled>
											Loading...
										</SelectItem>
									)}
								</SelectContent>
							</Select>
						</div>
					)}
					<NavUser user={user} onLogout={onLogout} />
				</SidebarFooter>
			</Sidebar>

			<SidebarInset>
				<header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b px-4 bg-transparent">
					<div className="flex items-center gap-2">
						<SidebarTrigger />
						<Separator orientation="vertical" className="h-6" />
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink onClick={() => navigate("/")}>
										Dashboard
									</BreadcrumbLink>
								</BreadcrumbItem>
								{currentPage && pathname !== "/dashboard" && (
									<>
										<BreadcrumbSeparator />
										<BreadcrumbItem>
											<BreadcrumbPage>{currentPage.name}</BreadcrumbPage>
										</BreadcrumbItem>
									</>
								)}
							</BreadcrumbList>
						</Breadcrumb>
					</div>
					<ThemeToggle />
				</header>
				<div className="flex flex-1 flex-col">
					{loading ? (
						<div
							role="status"
							aria-live="polite"
							className="flex items-center justify-center h-full"
						>
							<Spinner
								aria-hidden="true"
								className="size-8 text-muted-foreground"
							/>
							<span className="sr-only">Loading dashboard</span>
						</div>
					) : (
						<div className="flex-1">{children}</div>
					)}
				</div>
			</SidebarInset>
		</SidebarProvider>
	);
}
